import React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../../hooks/useLocalizedPath.jsx';
import { ArrowIcon } from '../sections/ArrowIcon.jsx';
import { Icon } from '../sections/Icon.jsx';
import { SectionTag } from '../sections/SectionTag.jsx';
import { SectionTitle } from '../sections/SectionTitle.jsx';
import { ACCENT_GRAD, FG_2, INK, LINE, PURPLE, RADII } from '../sections/theme.js';

export function ProductFamilySection({ background = '#fff', credentialsHref }) {
    const { t } = useTranslation();
    const { features, skillsGraphs, skillsIntelligence } = useLocalizedPath();

    const products = [
        { key: 'credentials', icon: 'badge', href: credentialsHref ?? features() },
        { key: 'skillsGraphs', icon: 'map', href: skillsGraphs() },
        { key: 'skillsIntelligence', icon: 'target', href: skillsIntelligence() },
    ];

    return (
        <Box component="section" id="productos" sx={{ py: { xs: 6, md: 10 }, background }}>
            <Container maxWidth="lg">
                <Stack
                    sx={{
                        alignItems: 'center',
                        textAlign: 'center',
                        mb: { xs: 4, md: 6 },
                    }}
                >
                    <SectionTag>{t('products.family.tag')}</SectionTag>
                    <SectionTitle sx={{ textAlign: 'center' }}>{t('products.family.title')}</SectionTitle>
                    <Typography sx={{ color: FG_2, fontSize: '1.0625rem', lineHeight: 1.7, maxWidth: 640 }}>
                        {t('products.family.text')}
                    </Typography>
                </Stack>
                <Grid container spacing={2.5}>
                    {products.map(({ key, icon, href }) => (
                        <Grid key={key} size={{ xs: 12, md: 4 }}>
                            <Box
                                component="a"
                                href={href}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    height: '100%',
                                    p: 3,
                                    background: '#fff',
                                    border: `1px solid ${LINE}`,
                                    borderRadius: RADII.lg,
                                    textDecoration: 'none',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 40px rgba(26,15,58,0.1)',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: RADII.sm,
                                        background: ACCENT_GRAD,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Icon name={icon} size={22} color="#fff" />
                                </Box>
                                <Typography component="h3" sx={{ fontSize: '1.2rem', fontWeight: 700, color: INK }}>
                                    {t(`products.family.${key}.title`)}
                                </Typography>
                                <Typography sx={{ color: FG_2, lineHeight: 1.65, flex: 1 }}>
                                    {t(`products.family.${key}.desc`)}
                                </Typography>
                                <Stack
                                    direction="row"
                                    spacing={0.5}
                                    sx={{
                                        alignItems: 'center',
                                        color: PURPLE,
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        sx={{ fontWeight: 700, fontSize: '0.95rem', color: PURPLE }}
                                    >
                                        {t(`products.family.${key}.cta`)}
                                    </Typography>
                                    <ArrowIcon size={18} />
                                </Stack>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
                <Typography
                    lang="en"
                    sx={{
                        mt: { xs: 4, md: 5 },
                        textAlign: 'center',
                        color: PURPLE,
                        fontWeight: 600,
                        fontStyle: 'italic',
                    }}
                >
                    {t('products.flywheel.tagline')}
                </Typography>
            </Container>
        </Box>
    );
}
